/**
 * Single sortable name from person name parts (first, middle, last, nickname).
 * Used for consistent alphabetical sorting.
 */
export function personSortName(parts: {
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  nickname?: string | null;
}): string {
  const first = (parts.first_name ?? '').trim();
  const middle = (parts.middle_name ?? '').trim();
  const last = (parts.last_name ?? '').trim();
  const combined = [first, middle, last].filter(Boolean).join(' ').trim();
  if (combined) {
    return combined;
  }
  return (parts.nickname ?? '').trim() || '';
}

/**
 * Exactly 2 initials: first letter of first name (first word) and first letter of last name (last word).
 * E.g. 'Joe Peter' + 'Smith' -> 'JS'; 'Joe' + 'Peter Smith' -> 'JS'.
 * Use when you have separate first/last name fields.
 */
export function getInitialsFromFirstLast(
  firstName: string,
  lastName: string,
): string {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  const firstWord = f.split(/\s+/).filter(Boolean)[0] ?? '';
  const lastWord = l.split(/\s+/).filter(Boolean).pop() ?? '';
  const a = firstWord.match(/[a-zA-Z]/)?.[0].toUpperCase() ?? '';
  const b = lastWord.match(/[a-zA-Z]/)?.[0].toUpperCase() ?? '';

  return a + b;
}

/**
 * Two initials from a full name: first letter of first word + first letter of last word.
 * E.g. 'Joe Peter Smith' → 'JS'. Single word → one letter. Uses only alphabetic characters.
 */
export function getInitials(name: string) {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return '';
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '';
  }

  const firstWord = words[0];
  const lastWord = words.length > 1 ? words[words.length - 1] : firstWord;
  const a = firstWord.match(/[a-zA-Z]/)?.[0].toUpperCase() ?? '';
  const b = lastWord.match(/[a-zA-Z]/)?.[0].toUpperCase() ?? '';

  return a + b;
}

export function getFullName({
  firstname,
  lastname,
  preferredname,
  fullname,
}: {
  firstname?: string;
  lastname?: string;
  preferredname?: string;
  fullname?: string;
}) {
  lastname = lastname ? lastname.trim() : '';
  fullname = fullname ? fullname.trim() : '';
  let s = (preferredname || firstname || '').trim();
  let count = s ? 1 : 0;
  if (lastname) {
    s += (s ? ' ' : '') + lastname;
    count++;
  }

  if ((!s || count < 2) && fullname) {
    s = fullname;
  }

  return s;
}

export function getLastName({
  lastname,
  fullname,
}: {
  lastname?: string;
  fullname?: string;
}) {
  if (lastname) {
    lastname = lastname.trim();
  }

  if (lastname) {
    return lastname;
  }

  if (fullname) {
    fullname = fullname.trim();
  }

  if (!fullname) {
    return '';
  }

  const index = fullname.lastIndexOf(' ');
  if (index === -1) {
    return '';
  }

  return fullname.substring(index + 1);
}

export function getFirstName({
  firstname,
  fullname,
}: {
  firstname?: string;
  fullname?: string;
}) {
  if (firstname) {
    firstname = firstname.trim();
  }

  if (firstname) {
    return firstname;
  }

  if (fullname) {
    fullname = fullname.trim();
  }

  if (!fullname) {
    return '';
  }

  const index = fullname.indexOf(' ');
  if (index === -1) {
    return fullname;
  }

  return fullname.substring(0, index);
}
